import React, { useState, useCallback, useEffect, useRef } from 'react'
import LeftBar from './LeftBar'
import MainContent from './MainContent'
import RightBar from './RightBar'
import { Search, Bell, Comments, Settings, Plus } from '../../../components/icons/Icons'
import { toast } from 'react-toastify'
import OriginalNotification from "../OriginalNotification"
import { useNavigate } from 'react-router-dom'
import { recentDraftListPath, announcementInteractionPath } from '../../../services/config'
import { ancemntNotification, initNotification, announcementInteraction, initInteractionState } from '../../../redux/slices/announcementSlice'
import { useSelect } from '../../../services/commonServices'
import { useDispatch } from 'react-redux'

// Parse user data once
const userData = JSON.parse(localStorage.getItem("user_data") || "{}")

// ─── Modal Component ────────────────────────────────────────────────
const CommentsModal = ({ announcementId, comments = [], onClose, onAddComment, onReplyComment, allowComments = true, allowReplies = true }) => {
  const [newComment, setNewComment] = useState('')
  const [replyText, setReplyText] = useState({})
  const [showReply, setShowReply] = useState({})

  const handlePost = () => {
    if (!allowComments) return
    const text = newComment.trim()
    if (!text) return
    onAddComment(announcementId, text)
    setNewComment('')
  }

  const handleReply = (parentId) => {
    const text = (replyText[parentId] || '').trim()
    if (!text) return
    onReplyComment(announcementId, parentId, text)
    setReplyText(prev => ({ ...prev, [parentId]: '' }))
    setShowReply(prev => ({ ...prev, [parentId]: false }))
  }

  const toggleReply = (id) =>
    setShowReply(prev => ({ ...prev, [id]: !prev[id] }))

  const avatarColors = [
    '#4f46e5', '#0f766e', '#b45309', '#7c3aed', '#be185d', '#dc2626', '#059669', '#d97706'
  ]

  const colorFor = (str = '') =>
    avatarColors[(str.charCodeAt(0) || 0) % avatarColors.length]

  const initials = (name = '') =>
    name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'U'

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
        ', ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    } catch {
      return dateStr
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, backdropFilter: 'blur(2px)'
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: '#09102b', borderRadius: '16px',
        width: '100%', maxWidth: '560px', maxHeight: '85vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 60px -16px rgba(0,0,0,0.65)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 20px', borderBottom: '1px solid rgba(79,70,229,0.15)',
          flexShrink: 0, background: 'rgb(29, 29, 57)'
        }}>
          <div>
            <span style={{ fontWeight: 600, fontSize: '16px', color: 'var(--primary-text-color, #f8fafc)' }}>
              Comments
            </span>
            <span style={{ fontSize: '13px', color: 'var(--secondary-text-color, #cbd5e1)', marginLeft: '6px' }}>
              ({comments.length})
            </span>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '24px', color: 'var(--secondary-text-color, #9ca3af)',
            padding: '0 4px', lineHeight: 1
          }}>×</button>
        </div>

        {/* Comments List */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '16px 20px',
          display: 'flex', flexDirection: 'column', gap: '20px',
          background: 'rgb(29, 29, 57)'
        }}>
          {comments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#cbd5e1' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>💬</div>
              <p style={{ margin: 0, fontSize: '14px' }}>No comments yet — be the first to comment!</p>
            </div>
          ) : (
            comments.map((comment) => {
              const commentId = comment.comment_id || comment.id
              const userName = comment.user_name || comment.userName || 'Anonymous'
              const userColor = colorFor(userName)

              return (
                <div key={commentId}>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: userColor + '20', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: '12px', fontWeight: 600, flexShrink: 0,
                      color: userColor
                    }}>
                      {initials(userName)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        background: '#111b3f',
                        border: '1px solid rgba(79,70,229,0.16)',
                        borderRadius: '0 12px 12px 12px',
                        padding: '10px 14px'
                      }}>
                        <div>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#f8fafc' }}>
                            {userName}
                          </span>
                          <span style={{ fontSize: '11px', color: 'var(--secondary-text-color, #9ca3af)', marginLeft: '10px' }}>
                            {formatDate(comment.created_at || comment.createdAt)}
                          </span>
                        </div>
                        <p style={{ margin: '6px 0 0 0', fontSize: '13px', lineHeight: 1.5, color: 'var(--primary-text-color, #f8fafc)' }}>
                          {comment.comment_text || comment.commentText || comment.comment || comment.text || comment.message || 'No comment text available'}
                        </p>
                      </div>

                      <button onClick={() => allowReplies && toggleReply(commentId)} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: '12px', color: '#8b5cf6', padding: '6px 0',
                        fontWeight: 500, marginTop: '2px'
                      }}>
                        {showReply[commentId] ? 'Cancel' : (allowReplies ? '↳ Reply' : 'Replies disabled')}
                      </button>

                      {/* Replies */}
                      {Array.isArray(comment.replies) && comment.replies.length > 0 && (
                        <div style={{ marginTop: '12px', paddingLeft: '16px', borderLeft: '2px solid rgba(79,70,229,0.3)' }}>
                          {comment.replies.map((reply) => {
                            const replyName = reply.user_name || reply.userName || 'Anonymous'
                            const replyColor = colorFor(replyName)
                            return (
                              <div key={reply.reply_id || reply.id} style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                                <div style={{
                                  width: 28, height: 28, borderRadius: '50%',
                                  background: replyColor + '20', display: 'flex',
                                  alignItems: 'center', justifyContent: 'center',
                                  fontSize: '10px', fontWeight: 600, flexShrink: 0,
                                  color: replyColor
                                }}>
                                  {initials(replyName)}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{
                                    background: '#111b3f',
                                    border: '1px solid rgba(79,70,229,0.12)',
                                    borderRadius: '0 10px 10px 10px',
                                    padding: '7px 12px'
                                  }}>
                                    <div>
                                      <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--primary-text-color, #f8fafc)' }}>
                                        {replyName}
                                      </span>
                                      <span style={{ fontSize: '10px', color: 'var(--secondary-text-color, #9ca3af)', marginLeft: '8px' }}>
                                        {formatDate(reply.created_at || reply.createdAt)}
                                      </span>
                                    </div>
                                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', lineHeight: 1.4, color: 'var(--primary-text-color, #f8fafc)' }}>
                                      {reply.comment_text || reply.commentText || reply.comment || reply.text || reply.message || 'No reply text available'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}

                      {/* Reply Input */}
                      {showReply[commentId] && allowReplies && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                          <input
                            value={replyText[commentId] || ''}
                            onChange={(e) => setReplyText(prev => ({ ...prev, [commentId]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleReply(commentId) }}
                            placeholder="Write a reply..."
                            style={{
                              flex: 1, fontSize: '13px', padding: '7px 12px',
                              borderRadius: '8px', border: '1px solid #4f46e5',
                              background: '#111827', color: '#f8fafc', outline: 'none'
                            }}
                            autoFocus
                          />
                          <button onClick={() => handleReply(commentId)} style={{
                            background: '#4f46e5', color: '#fff', border: 'none',
                            borderRadius: '8px', padding: '6px 16px', cursor: 'pointer',
                            fontSize: '12px', fontWeight: 500
                          }}>Send</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* Footer - Add Comment */}
        <div style={{
          padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0, display: 'flex', gap: '10px', background: '#0f172a'
        }}>
          <textarea
            rows={1}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost() } }}
            placeholder="Write a comment..."
            style={{
              flex: 1, fontSize: '13px', padding: '9px 12px',
              borderRadius: '10px', border: '1px solid #4f46e5',
              background: '#111827', color: '#f8fafc',
              resize: 'none', outline: 'none', fontFamily: 'inherit'
            }}
          />
          <button
            onClick={handlePost}
            disabled={!newComment.trim()}
            style={{
              background: newComment.trim() ? '#7c3aed' : '#334155',
              color: '#fff', border: 'none', borderRadius: '10px', padding: '0 22px',
              cursor: newComment.trim() ? 'pointer' : 'not-allowed',
              opacity: newComment.trim() ? 1 : 0.65,
              fontSize: '13px', fontWeight: 500, transition: 'all 0.2s'
            }}
          >
            Post
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Helper: normalise any raw API response into a typed object ──────────────
// Priority order matters:
//   1. If it has an explicit "action" field → trust it
//   2. If it looks like a comments array → get_comments
//   3. If it has likes/dislikes fields → get_counts
//   4. Otherwise return as-is
const normalizeResponse = (rawResponse) => {
  if (!rawResponse) return null

  // Trust explicit action field first
  if (rawResponse.action) return rawResponse

  // Array at root → comments list
  if (Array.isArray(rawResponse)) {
    return { action: "get_comments", comments: rawResponse }
  }

  // Handle response with data property
  if (rawResponse.data && typeof rawResponse.data === 'object') {
    const innerData = rawResponse.data
    
    // Counts nested inside data object
    if (innerData.likes !== undefined || innerData.dislikes !== undefined || 
        innerData.comments !== undefined || innerData.user_reaction !== undefined) {
      return {
        action: "get_counts",
        announcement_id: rawResponse.announcement_id || innerData.announcement_id,
        likes: innerData.likes || 0,
        dislikes: innerData.dislikes || 0,
        comments: innerData.comments || 0,
        shares: innerData.shares || 0,
        user_reaction: innerData.user_reaction || null
      }
    }
    
    // Nested comments in data object
    if (Array.isArray(innerData.comments)) {
      return { action: "get_comments", comments: innerData.comments }
    }
    if (Array.isArray(innerData.data)) {
      return { action: "get_comments", comments: innerData.data }
    }
  }

  // Nested comments arrays
  if (Array.isArray(rawResponse.comments)) {
    return { action: "get_comments", comments: rawResponse.comments }
  }

  // Array data with comment_id → comments list
  if (Array.isArray(rawResponse.data) && rawResponse.data.length > 0 && rawResponse.data[0]?.comment_id) {
    return { action: "get_comments", comments: rawResponse.data }
  }

    // Flat likes/dislikes (direct response)
    if (rawResponse.likes !== undefined || rawResponse.dislikes !== undefined || 
        rawResponse.user_reaction !== undefined || rawResponse.comments !== undefined) {
      return {
        action: "get_counts",
        announcement_id: rawResponse.announcement_id,
        likes: rawResponse.likes || 0,
        dislikes: rawResponse.dislikes || 0,
        comments: rawResponse.comments || 0,
        shares: rawResponse.shares || 0,
        user_reaction: rawResponse.user_reaction || null,
        // Pass through meta fields from the request so the handler can correlate counts to posts
        _meta_id: rawResponse._meta_id,
        _meta_action: rawResponse._meta_action
      }
    }

    // 🐛 FIX: If the response has _meta_action attached (from the slice fix), use it as the action
    // This handles share_announcement, toggle_reaction, add_comment, reply_comment responses
    // that don't match any of the patterns above.
    if (rawResponse._meta_action) {
      return {
        ...rawResponse,
        action: rawResponse._meta_action,
      }
    }

    return rawResponse
}

// ─── Main Index Component ────────────────────────────────────────────────────
const Index = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const socialHubStore = useSelect("announce")

  const [activeTab, setActiveTab] = useState("")
  const [collapsed, setCollapsed] = useState(false)
  const [socialData, setSocialData] = useState([])
  const [trendingTags, setTrendingTags] = useState([])
  const [mostEngaged, setMostEngaged] = useState([])
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [yourActivity, setYourActivity] = useState([])
  const [connectWith, setConnectWith] = useState([])
  const [searchHubs, setSearchHubs] = useState('')
  const [interactionCounts, setInteractionCounts] = useState({})

  // ✅ Refs to avoid stale closure bugs in async dispatch responses
 const pendingMap = useRef({}) // { [announcement_id + action]: true }
const lastActionAnnouncementIdRef = useRef(null)
  // Comments modal state
  const [comments, setComments] = useState([])
  const [commentsModalId, setCommentsModalId] = useState(null)
  const [commentsModalFlags, setCommentsModalFlags] = useState({ allowComments: true, allowReplies: true })

  // ── Fetch announcement list on mount ──────────────────────────────
  const getTemplateList = useCallback(() => {
    dispatch(ancemntNotification({
      path: recentDraftListPath,
      data: { userId: userData.user_id, isDraft: "2" },
    }))
  }, [dispatch])

  useEffect(() => { getTemplateList() }, [getTemplateList])

  // ── Handle announcement list response + auto-fetch counts ─────────
  useEffect(() => {
  if (socialHubStore?.ancemntNotificationSuccess) {
    const data = socialHubStore.ancemntNotificationData || []
    setSocialData(data)
    dispatch(initNotification())

    // Auto-fetch counts for every post
    data.forEach((post) => {
      const id = post.announcement_id || post.id
      if (id) {
        console.log(`🔍 Fetching counts for announcement ${id}`)
        pendingMap.current[`${id}_get_counts`] = id
        dispatch(announcementInteraction({
          path: announcementInteractionPath,
          data: { 
            action: "get_counts", 
            announcement_id: id, 
            user_id: userData.user_id, 
            _meta_id: id, 
            _meta_action: "get_counts" 
          },
        }))
      }
    })
  }
}, [socialHubStore?.ancemntNotificationSuccess, socialHubStore?.ancemntNotificationData, dispatch])

  // ── Handle all interaction API responses ──────────────────────────
// Handle all interaction API responses
  // 🐛 FIX: Sync counts from Redux state (atomically accumulated in the reducer,
  // avoiding race conditions from multiple rapid API responses)
  useEffect(() => {
    if (socialHubStore?.interactionCountsMap && Object.keys(socialHubStore.interactionCountsMap).length > 0) {
      setInteractionCounts(prev => ({
        ...prev,
        ...socialHubStore.interactionCountsMap
      }))
    }
  }, [socialHubStore?.interactionCountsMap])

  useEffect(() => {
  if (socialHubStore?.interactionSuccess) {
    const rawResponse = socialHubStore.interactionData
    const response = normalizeResponse(rawResponse)

    if (response) {
      const action = response.action
      const metaId = rawResponse?._meta_id || response?._meta_id
      const metaAction = rawResponse?._meta_action || response?._meta_action
      
      // Handle GET COUNTS response
      // Note: counts are now also accumulated directly in the Redux reducer (interactionCountsMap)
      // to avoid race conditions. This handler only processes non-counts actions.
      if (action === "get_counts" || metaAction === "get_counts") {
        const resolvedId = metaId || response.announcement_id
        if (resolvedId) {
          delete pendingMap.current[`${resolvedId}_get_counts`]
        }
      }
      
      // Handle GET COMMENTS response
      else if (action === "get_comments" || metaAction === "get_comments") {
        const list = (() => {
          if (Array.isArray(response.comments)) return response.comments
          if (Array.isArray(response.data)) return response.data
          if (Array.isArray(response.data?.data)) return response.data.data
          if (Array.isArray(response.data?.comments)) return response.data.comments
          if (Array.isArray(response)) return response
          return []
        })()
        
        console.log(`📊 Got ${list.length} comments`)
        setComments(list)
      }

      // Handle TOGGLE REACTION response
      else if (action === "toggle_reaction") {
        toast.success(response.message || "Reaction updated")
        const actionId = lastActionAnnouncementIdRef.current
        if (actionId) {
          // Refresh counts after reaction
          setTimeout(() => {
            pendingMap.current[`${actionId}_get_counts`] = actionId
            dispatch(announcementInteraction({
              path: announcementInteractionPath,
              data: { 
                action: "get_counts", 
                announcement_id: actionId, 
                user_id: userData.user_id, 
                _meta_id: actionId, 
                _meta_action: "get_counts" 
              },
            }))
          }, 100)
          lastActionAnnouncementIdRef.current = null
        }
      }

      // Handle ADD COMMENT response
      else if (action === "add_comment") {
        toast.success(response.message || "Comment added successfully")
        const actionId = lastActionAnnouncementIdRef.current
        if (actionId) {
          // Refresh counts and comments
          setTimeout(() => {
            pendingMap.current[`${actionId}_get_counts`] = actionId
            dispatch(announcementInteraction({
              path: announcementInteractionPath,
              data: { 
                action: "get_counts", 
                announcement_id: actionId, 
                user_id: userData.user_id, 
                _meta_id: actionId, 
                _meta_action: "get_counts" 
              },
            }))
            dispatch(announcementInteraction({
              path: announcementInteractionPath,
              data: { 
                action: "get_comments", 
                announcement_id: actionId, 
                page: 1, 
                limit: 100, 
                _meta_id: actionId, 
                _meta_action: "get_comments" 
              },
            }))
          }, 100)
          lastActionAnnouncementIdRef.current = null
        }
      }

      // Handle REPLY COMMENT response
      else if (action === "reply_comment") {
        toast.success(response.message || "Reply added successfully")
        const actionId = lastActionAnnouncementIdRef.current
        if (actionId) {
          // Refresh counts and comments
          setTimeout(() => {
            pendingMap.current[`${actionId}_get_counts`] = actionId
            dispatch(announcementInteraction({
              path: announcementInteractionPath,
              data: { 
                action: "get_counts", 
                announcement_id: actionId, 
                user_id: userData.user_id, 
                _meta_id: actionId, 
                _meta_action: "get_counts" 
              },
            }))
            dispatch(announcementInteraction({
              path: announcementInteractionPath,
              data: { 
                action: "get_comments", 
                announcement_id: actionId, 
                page: 1, 
                limit: 100, 
                _meta_id: actionId, 
                _meta_action: "get_comments" 
              },
            }))
          }, 100)
          lastActionAnnouncementIdRef.current = null
        }
      }

      // Handle SHARE ANNOUNCEMENT response
      else if (action === "share_announcement") {
        toast.success(response.message || "Announcement shared successfully")
        const actionId = lastActionAnnouncementIdRef.current || response.announcement_id
        if (actionId) {
          // Refresh counts after share
          setTimeout(() => {
            pendingMap.current[`${actionId}_get_counts`] = actionId
            dispatch(announcementInteraction({
              path: announcementInteractionPath,
              data: { 
                action: "get_counts", 
                announcement_id: actionId, 
                user_id: userData.user_id, 
                _meta_id: actionId, 
                _meta_action: "get_counts" 
              },
            }))
          }, 100)
          lastActionAnnouncementIdRef.current = null
        }
      }
    }

    dispatch(initInteractionState())
  } else if (socialHubStore?.interactionError) {
    console.error("❌ Interaction Error:", socialHubStore.interactionMessage)
    toast.error(socialHubStore.interactionMessage || "An error occurred")
    dispatch(initInteractionState())
  }
}, [
  socialHubStore?.interactionSuccess,
  socialHubStore?.interactionError,
  socialHubStore?.interactionData,
  socialHubStore?.interactionMessage,
  dispatch,
])
  // ── Interaction Handlers ──────────────────────────────────────────

  const handleToggleReaction = useCallback((announcementId, reactionType) => {
    lastActionAnnouncementIdRef.current = announcementId

    // Optimistic UI update
    setInteractionCounts(prev => {
      const current = prev[announcementId] || { likes: 0, dislikes: 0, comments: 0, user_reaction: null }
      let likes = current.likes || 0
      let dislikes = current.dislikes || 0
      let user_reaction = current.user_reaction

      if (reactionType === "like") {
        if (user_reaction === "like") { likes = Math.max(0, likes - 1); user_reaction = null }
        else { if (user_reaction === "dislike") dislikes = Math.max(0, dislikes - 1); likes += 1; user_reaction = "like" }
      } else if (reactionType === "dislike") {
        if (user_reaction === "dislike") { dislikes = Math.max(0, dislikes - 1); user_reaction = null }
        else { if (user_reaction === "like") likes = Math.max(0, likes - 1); dislikes += 1; user_reaction = "dislike" }
      }

      return { ...prev, [announcementId]: { ...current, likes, dislikes, user_reaction } }
    })

    dispatch(announcementInteraction({
      path: announcementInteractionPath,
      data: { action: "toggle_reaction", announcement_id: announcementId, reaction_type: reactionType, user_id: userData.user_id },
    }))
  }, [dispatch])

  const handleAddComment = useCallback((announcementId, commentText) => {
    // Optimistic UI update
    const tempComment = {
      comment_id: `temp-${Date.now()}`,
      user_name: userData.full_name || userData.user_name || "You",
      comment_text: commentText,
      created_at: new Date().toISOString(),
      replies: [],
    }
    setComments(prev => [...prev, tempComment])
    setInteractionCounts(prev => ({
      ...prev,
      [announcementId]: { ...prev[announcementId], comments: (prev[announcementId]?.comments || 0) + 1 },
    }))

    lastActionAnnouncementIdRef.current = announcementId
    dispatch(announcementInteraction({
      path: announcementInteractionPath,
      data: { action: "add_comment", announcement_id: announcementId, comment_text: commentText, user_id: userData.user_id },
    }))
  }, [dispatch])

  const handleReplyComment = useCallback((announcementId, parentCommentId, commentText) => {
    lastActionAnnouncementIdRef.current = announcementId
    dispatch(announcementInteraction({
      path: announcementInteractionPath,
      data: { action: "reply_comment", announcement_id: announcementId, parent_comment_id: parentCommentId, comment_text: commentText, user_id: userData.user_id },
    }))
  }, [dispatch])

  // ✅ KEY FIX: get_comments sets pendingActionTypeRef so the response handler
  //    knows to call setComments, not setInteractionCounts
const handleGetComments = useCallback((announcementId, page = 1, limit = 100) => {
  dispatch(announcementInteraction({
    path: announcementInteractionPath,
    data: { action: "get_comments", announcement_id: announcementId, page, limit, _meta_id: announcementId, _meta_action: "get_comments" },
  }))
}, [dispatch])

const handleGetCounts = useCallback((announcementId) => {
  if (!announcementId) return;
  
  // Set pending flag
  pendingMap.current[`${announcementId}_get_counts`] = announcementId;
  
  dispatch(announcementInteraction({
    path: announcementInteractionPath,
    data: { 
      action: "get_counts", 
      announcement_id: announcementId, 
      user_id: userData.user_id,
      _meta_id: announcementId, 
      _meta_action: "get_counts" 
    },
  }));
}, [dispatch]);
  const handleShareAnnouncement = useCallback((announcementId, sharedWith) => {
    lastActionAnnouncementIdRef.current = announcementId
    dispatch(announcementInteraction({
      path: announcementInteractionPath,
      data: { action: "share_announcement", announcement_id: announcementId, shared_with: sharedWith, shared_by: userData.user_id, _meta_id: announcementId, _meta_action: "share_announcement" },
    }))
  }, [dispatch])

  // Edit / Delete / Tag handlers for posts (called from PostCard menu)
  const handleEditPost = useCallback((post) => {
    const id = post?.announcement_id || post?.announceId || post?.id
    if (!id) return
    // Navigate to add-announcement and pass id in location state; AddNotification can read location.state to prefill
    navigate('/add-announcement', { state: { editAnnouncementId: id } })
  }, [navigate])

  const handleDeletePost = useCallback((postId) => {
    if (!postId) return
    if (!window.confirm('Are you sure you want to delete this post?')) return
    toast.info('Delete request sent (not implemented server-side)')
    console.log('Delete post requested:', postId)
    // TODO: wire up delete API when backend supports it
  }, [])

  const handleOpenComments = useCallback((announcementId, flags = {}) => {
    setComments([])
    setCommentsModalId(announcementId)
    setCommentsModalFlags({
      allowComments: flags.allowComments !== undefined ? flags.allowComments : true,
      allowReplies: flags.allowReplies !== undefined ? flags.allowReplies : true,
    })
    // ✅ This now correctly sets pendingActionTypeRef = "get_comments"
    handleGetComments(announcementId)
  }, [handleGetComments])

  const handleCloseComments = useCallback(() => {
    setCommentsModalId(null)
    setComments([])
  }, [])

  // Tab handlers
  const handleActiveTab = (item) => {
    setActiveTab(item)
    const systemTabs = ["leave", "offsite", "regularisation", "halfDay"]
    setCollapsed(systemTabs.includes(item))
  }

  const navigateToAddAnnouncement = () => { navigate("/add-announcement") }

  const isSystemTab = ["leave", "offsite", "regularisation", "halfDay"].includes(activeTab)

  // Filter posts by search
  // Apply category filter (from LeftBar) and text search
  const applyCategoryFilter = (list, category) => {
    if (!category) return list
    const cat = String(category).toLowerCase()
    if (cat === "all posts" || cat === "allposts" || cat === "all" || cat === "allposts") return list

    // Pinned posts
    if (cat.includes("pinned")) {
      return list.filter(p => String(p.pinTopFeed || p.pin_top_feed || p.pinTop || p.pin)?.trim() === "1" || p.is_pinned === true)
    }

    // Normalize post fields to a single searchable string
    return list.filter((post) => {
      const fields = [post.subject, post.announceTypeDesc, post.announceTypeName, post.announceType, post.bodyContent, post.postedBy]
        .filter(Boolean)
        .map(f => String(f).toLowerCase())
        .join(" ")

      // Match label words (e.g., 'birthday' should match 'birthdays')
      const key = cat.replace(/\s+/g, " ")
      return fields.includes(key) || fields.includes(key.replace(/s$/, ""))
    })
  }

  const categoryFiltered = applyCategoryFilter(socialData, activeTab)

  const filteredSocialData = searchHubs.trim()
    ? categoryFiltered.filter((post) => {
        const q = searchHubs.toLowerCase()
        return (
          (post.subject || "").toLowerCase().includes(q) ||
          (post.announceTypeDesc || "").toLowerCase().includes(q) ||
          (post.announceTypeName || "").toLowerCase().includes(q) ||
          (post.announceType || "").toLowerCase().includes(q)
        )
      })
    : categoryFiltered

  return (
    <div className='section-sosial-hub' style={{ marginTop: isSystemTab ? "-20px" : "-40px" }}>

      {/* Header */}
      <div className='social-hub-header'>
        <h3 className='hub-title color-white font-weight-200'>Employee Social Hub</h3>
        {!isSystemTab && (
          <>
            <div className='hub-search-container'>
              <input
                value={searchHubs}
                onChange={(e) => setSearchHubs(e.target.value)}
                placeholder='Search posts, tags or people'
                className='universal-input'
              />
              <Search class_name={"search-icon"} />
            </div>
            <div className='notification-panel'>
              <span><Bell class_name='color-white icon' /></span>
              <span><Comments class_name='color-white icon' /></span>
              <span><Settings class_name='color-white icon' /></span>
              <button type="button" className="universal-btn border-radius-8px" onClick={navigateToAddAnnouncement}>
                <Plus /> Add Announcement
              </button>
            </div>
          </>
        )}
      </div>

      {/* Body */}
      <div style={{ display: "flex", width: "100%", alignItems: "flex-start" }}>
        <LeftBar
          activeTab={activeTab}
          handleActiveTab={handleActiveTab}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
        />

        <div style={{ flex: 1, minWidth: 0, transition: "all 0.3s ease", overflow: "hidden" }}>
          {isSystemTab ? (
            <div style={{ padding: "0 10px", width: "100%" }}>
              <OriginalNotification active_tab={activeTab} />
            </div>
          ) : (
            <div style={{ display: "flex", gap: "10px", padding: "0 10px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {Array.isArray(filteredSocialData) && filteredSocialData.length > 0 ? (
               <MainContent
  socialData={filteredSocialData}
  onToggleReaction={handleToggleReaction}
  onAddComment={handleAddComment}
  onReplyComment={handleReplyComment}
  onGetComments={handleGetComments}
  onGetCounts={handleGetCounts}
  onShareAnnouncement={handleShareAnnouncement}
  onOpenComments={handleOpenComments}
  interactionCounts={interactionCounts}  // ✅ This should be passed
                onEditPost={handleEditPost}
                onDeletePost={handleDeletePost}
/>
                ) : searchHubs.trim() ? (
                  <div style={{
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    padding: "60px 20px", gap: "12px", opacity: 0.6,
                  }}>
                    <span style={{ fontSize: "40px" }}>🔍</span>
                    <p style={{ color: "var(--primary-text-color)", fontSize: "15px", margin: 0 }}>
                      No results for "<strong>{searchHubs}</strong>"
                    </p>
                    <p style={{ color: "var(--secondary-text-color)", fontSize: "13px", margin: 0 }}>
                      Try searching by subject, tag or post content
                    </p>
                  </div>
                ) : (
                  <div style={{
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    padding: "80px 20px", gap: "16px"
                  }}>
                    <span style={{ fontSize: "48px" }}>📢</span>
                    <p style={{ color: "var(--secondary-text-color)", fontSize: "14px", margin: 0 }}>
                      No announcements yet
                    </p>
                  </div>
                )}
              </div>

              <div style={{ width: "260px", flexShrink: 0 }}>
                <RightBar
                  handleActiveTab={handleActiveTab}
                  trendingTags={trendingTags}
                  mostEngaged={mostEngaged}
                  upcomingEvents={upcomingEvents}
                  yourActivity={yourActivity}
                  connectWith={connectWith}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Comments Modal */}
      {commentsModalId && (
        <CommentsModal
          announcementId={commentsModalId}
          comments={comments}
          onClose={handleCloseComments}
          onAddComment={handleAddComment}
          onReplyComment={handleReplyComment}
          allowComments={commentsModalFlags.allowComments}
          allowReplies={commentsModalFlags.allowReplies}
        />
      )}
    </div>
  )
}

export default Index